import React, { useCallback, useEffect, useState } from "react";
import { Routes, Route, Navigate, NavLink, useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, Disc3, CalendarClock, Film, ShoppingBag, FileText, Link2, Image, Users, Settings as Cog, LogOut, Plus, Pencil, Trash2, RotateCcw, Music, ExternalLink, Mail, TrendingUp, X, Share2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { adminApi, publicApi, mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { Field, Text, Select, PasswordInput, Btn, StatusPill, panel } from "@/admin/kit";
import { ReleaseEditor, MerchEditor, MediaLibrary } from "@/admin/editors";
import { UnassignedVideos } from "@/admin/unassignedVideos";
import { PreflightBadge } from "@/admin/preflight";
import { FanDots } from "@/components/site/FanDots";
import { fmtShortDate, countdownParts } from "@/lib/release";

// ------------------------------------------------------------- Login
export const ADMIN_BUILD = "v4.60 · 2FA";

const Login = () => {
  const { login, verifyCode, resendCode, user, isDeviceRemembered, forgetThisDevice } = useAuth();
  const nav = useNavigate();
  const [deviceRemembered, setDeviceRemembered] = useState(isDeviceRemembered());
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("signin"); // signin | code | forgot
  const [challenge, setChallenge] = useState(null);
  const [emailHint, setEmailHint] = useState("");
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(true);
  const [resendBusy, setResendBusy] = useState(false);
  const [fpEmail, setFpEmail] = useState("");
  const [fpBusy, setFpBusy] = useState(false);
  const [fpSent, setFpSent] = useState(false);
  useEffect(() => { if (user) nav("/admin/dashboard"); }, [user, nav]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, pw);
    setBusy(false);
    if (res.ok) return nav("/admin/dashboard");
    if (res.requiresCode) { setChallenge(res.challenge); setEmailHint(res.emailHint); setCode(""); setMode("code"); return; }
    toast.error(res.error);
  };
  const submitCode = async (e) => {
    e.preventDefault();
    if (code.trim().length !== 6) return toast.error("Enter the 6-digit code from your email");
    setBusy(true);
    const res = await verifyCode(challenge, code, remember);
    setBusy(false);
    if (res.ok) return nav("/admin/dashboard");
    toast.error(res.error);
    if (res.status === 400 || res.status === 429) { setMode("signin"); setChallenge(null); }
  };
  const resend = async () => {
    setResendBusy(true);
    const res = await resendCode(challenge);
    setResendBusy(false);
    if (res.ok) { setChallenge(res.challenge); toast.success("A new code is on its way"); }
    else toast.error(res.error);
  };
  const submitForgot = async (e) => {
    e.preventDefault();
    if (!fpEmail) return toast.error("Enter your email");
    setFpBusy(true);
    try {
      await publicApi.post("/public/auth/forgot-password", { email: fpEmail });
      setFpSent(true);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setFpBusy(false); }
  };
  return (
    <div className="min-h-screen grid place-items-center px-6" style={{ backgroundColor: "var(--ink)" }}>
      <div className="w-full max-w-sm">
        <img src="/assets/img/logo.png" alt="Heart N Soul Records" className="h-16 mx-auto mb-8" />
        <div className="frame p-8" style={{ backgroundColor: "var(--ink-raised)" }}>
          {mode === "code" ? (
            <>
              <p className="font-caps text-gold text-center mb-3" style={{ fontSize: ".68rem" }}>Check your email</p>
              <p className="text-center mb-6" style={{ color: "var(--ash)", fontSize: ".8rem" }} data-testid="code-hint">
                We emailed a 6-digit sign-in code to <span style={{ color: "var(--parchment)" }}>{emailHint}</span>. It expires in 10 minutes.
              </p>
              <form onSubmit={submitCode} className="grid gap-4" data-testid="code-form">
                <Field label="Sign-in code">
                  <Text inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} autoFocus
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="login-code"
                        style={{ letterSpacing: ".4em", textAlign: "center", fontSize: "1.4rem" }} />
                </Field>
                <label className="flex items-center gap-3 cursor-pointer" style={{ color: "var(--parchment)", fontSize: ".8rem" }}>
                  <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="remember-device" />
                  Remember this device for 30 days
                </label>
                <Btn type="submit" disabled={busy} data-testid="code-submit" style={{ justifyContent: "center" }}>{busy ? "Checking…" : "Verify & sign in"}</Btn>
              </form>
              <div className="flex justify-between mt-4 font-caps" style={{ fontSize: ".6rem" }}>
                <button type="button" onClick={resend} disabled={resendBusy} data-testid="resend-code"
                        style={{ color: "var(--gold)", background: "none", border: "none", cursor: "pointer", letterSpacing: ".18em" }}>
                  {resendBusy ? "Sending…" : "Resend code"}
                </button>
                <button type="button" onClick={() => { setMode("signin"); setChallenge(null); }} data-testid="back-to-signin"
                        style={{ color: "var(--ash)", background: "none", border: "none", cursor: "pointer", letterSpacing: ".18em" }}>
                  Back
                </button>
              </div>
            </>
          ) : mode === "signin" ? (
            <>
              <p className="font-caps text-gold text-center mb-6" style={{ fontSize: ".68rem" }}>Owner Login</p>
              <form onSubmit={submit} className="grid gap-4" data-testid="login-form">
                <Field label="Email"><Text type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" /></Field>
                <Field label="Password"><PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} data-testid="login-password" testidToggle="login-password-toggle" /></Field>
                <Btn type="submit" disabled={busy} data-testid="login-submit" style={{ justifyContent: "center" }}>{busy ? "Signing in…" : "Sign in"}</Btn>
              </form>
              {deviceRemembered && (
                <div className="mt-4 p-3 text-center" style={{ border: "1px solid rgba(201,162,74,.35)", fontSize: ".72rem", color: "var(--ash)", lineHeight: 1.5 }} data-testid="device-remembered-note">
                  This browser is remembered, so no sign-in code will be asked.{" "}
                  <button type="button" onClick={() => { forgetThisDevice(); setDeviceRemembered(false); toast.success("This browser will ask for a code next time"); }}
                          data-testid="forget-this-device"
                          style={{ color: "var(--gold)", background: "none", border: "none", cursor: "pointer", textDecoration: "underline", fontSize: "inherit" }}>
                    Ask for a code on this browser
                  </button>
                </div>
              )}
              <div className="text-center mt-4">
                <button type="button" onClick={() => { setMode("forgot"); setFpEmail(email); }} data-testid="forgot-password-link"
                        className="font-caps"
                        style={{ fontSize: ".6rem", color: "var(--ash)", background: "none", border: "none", cursor: "pointer", letterSpacing: ".18em" }}>
                  Forgot password?
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="font-caps text-gold text-center mb-2" style={{ fontSize: ".68rem" }}>Reset password</p>
              {fpSent ? (
                <>
                  <p className="text-center mb-6" style={{ color: "var(--parchment)", fontSize: ".9rem", lineHeight: 1.5 }} data-testid="forgot-sent">
                    If an account exists for that email, a reset link is on its way. Check your inbox (and spam).
                  </p>
                  <Btn type="button" onClick={() => { setMode("signin"); setFpSent(false); }} style={{ justifyContent: "center", width: "100%" }}>Back to sign in</Btn>
                </>
              ) : (
                <>
                  <p className="text-center mb-6" style={{ color: "var(--ash)", fontSize: ".8rem" }}>Enter your account email and we&apos;ll send a reset link.</p>
                  <form onSubmit={submitForgot} className="grid gap-4" data-testid="forgot-form">
                    <Field label="Email"><Text type="email" value={fpEmail} onChange={(e) => setFpEmail(e.target.value)} data-testid="forgot-email" /></Field>
                    <Btn type="submit" disabled={fpBusy} data-testid="forgot-submit" style={{ justifyContent: "center" }}>{fpBusy ? "Sending…" : "Send reset link"}</Btn>
                  </form>
                  <div className="text-center mt-4">
                    <button type="button" onClick={() => setMode("signin")} className="font-caps"
                            style={{ fontSize: ".6rem", color: "var(--ash)", background: "none", border: "none", cursor: "pointer", letterSpacing: ".18em" }}>
                      Back to sign in
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
        <p className="font-caps text-center mt-4" style={{ fontSize: ".5rem", color: "var(--ash)", opacity: .7 }} data-testid="admin-build">{ADMIN_BUILD}</p>
      </div>
    </div>
  );
};

// ------------------------------------------------------------- Reset Password (from emailed link)
const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    setBusy(true);
    try {
      await publicApi.post("/public/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/admin"), 2000);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!token) {
    return <div className="min-h-screen grid place-items-center" style={{ backgroundColor: "var(--ink)", color: "var(--ash)" }}>
      Missing reset token. <Link to="/admin" className="text-gold ml-2">Back to login</Link>
    </div>;
  }
  return (
    <div className="min-h-screen grid place-items-center px-6" style={{ backgroundColor: "var(--ink)" }}>
      <div className="w-full max-w-sm">
        <img src="/assets/img/logo.png" alt="Heart N Soul Records" className="h-16 mx-auto mb-8" />
        <div className="frame p-8" style={{ backgroundColor: "var(--ink-raised)" }}>
          <p className="font-caps text-gold text-center mb-6" style={{ fontSize: ".68rem" }}>Choose a new password</p>
          {done ? (
            <p className="text-center py-4" style={{ color: "var(--gold)", fontSize: "1.1rem", fontFamily: "var(--display)" }} data-testid="reset-done">
              Password updated. Redirecting…
            </p>
          ) : (
            <form onSubmit={submit} className="grid gap-4" data-testid="reset-form">
              <Field label="New password (min 8 characters)"><PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password" /></Field>
              <Btn type="submit" disabled={busy} data-testid="reset-submit" style={{ justifyContent: "center" }}>{busy ? "Saving…" : "Set new password"}</Btn>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------- Forced password change
const ForceChange = () => {
  const { changePassword } = useAuth();
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await changePassword({ new_password: pw });
    setBusy(false);
    if (res.ok) toast.success("Password updated");
    else toast.error(res.error);
  };
  return (
    <div className="min-h-screen grid place-items-center px-6" style={{ backgroundColor: "var(--ink)" }}>
      <div className="w-full max-w-sm frame p-8" style={{ backgroundColor: "var(--ink-raised)" }}>
        <p className="font-caps text-gold text-center mb-2" style={{ fontSize: ".68rem" }}>Set your password</p>
        <p className="text-center mb-6" style={{ color: "var(--ash)", fontSize: ".8rem" }}>For security, choose a new password before continuing.</p>
        <form onSubmit={submit} className="grid gap-4" data-testid="force-change-form">
          <Field label="New password (min 8 characters)"><PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} data-testid="new-password" /></Field>
          <Btn type="submit" disabled={busy} style={{ justifyContent: "center" }}>{busy ? "Saving…" : "Save password"}</Btn>
        </form>
      </div>
    </div>
  );
};

// ------------------------------------------------------------- Layout
const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/releases", label: "Releases", icon: Disc3 },
  { to: "/admin/upcoming", label: "Upcoming", icon: CalendarClock },
  { to: "/admin/merch", label: "Merch", icon: ShoppingBag },
  { to: "/admin/orders", label: "Orders", icon: TrendingUp },
  { to: "/admin/pages", label: "Pages", icon: FileText },
  { to: "/admin/links", label: "Links", icon: Link2 },
  { to: "/admin/media", label: "Media", icon: Image },
  { to: "/admin/subscribers", label: "Subscribers", icon: Users },
  { to: "/admin/settings", label: "Settings", icon: Cog },
];

const AdminLayout = ({ children }) => {
  const { logout, user } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--ink)" }}>
      <div className="lg:grid" style={{ gridTemplateColumns: "240px 1fr" }}>
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-0 lg:h-screen p-5 flex lg:flex-col gap-1 overflow-x-auto lg:overflow-y-auto no-scrollbar" style={{ borderRight: "var(--rule)", backgroundColor: "var(--ink-raised)" }}>
          <Link to="/admin/dashboard" className="hidden lg:flex items-center gap-2 mb-6"><img src="/assets/img/logo.png" alt="" className="h-9" /><span className="font-caps text-gold" style={{ fontSize: ".56rem" }}>Studio</span></Link>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="view-site-btn"
            className="flex items-center gap-3 px-3 py-2.5 font-caps mb-3"
            style={{
              fontSize: ".58rem",
              color: "var(--gold)",
              border: "1px solid var(--gold)",
              borderRadius: "var(--radius)",
              background: "transparent",
              whiteSpace: "nowrap",
              transition: "background .2s, color .2s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "var(--gold)"; e.currentTarget.style.color = "var(--ink)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--gold)"; }}
          >
            <ExternalLink size={14} /> View site
          </a>
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`anav-${n.label.toLowerCase()}`}
              className="flex items-center gap-3 px-3 py-2.5 shrink-0 font-caps"
              style={({ isActive }) => ({ fontSize: ".58rem", borderRadius: "var(--radius)", whiteSpace: "nowrap",
                color: isActive ? "var(--ink)" : "var(--parchment)", background: isActive ? "var(--gold)" : "transparent" })}>
              <n.icon size={15} /> {n.label}
            </NavLink>
          ))}
          <button onClick={logout} className="flex items-center gap-3 px-3 py-2.5 font-caps mt-2" style={{ fontSize: ".58rem", color: "var(--ash)", background: "none", border: "none", cursor: "pointer" }} data-testid="logout-btn"><LogOut size={15} /> Log out</button>
        </aside>
        {/* Content */}
        <main className="p-5 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            <a href="/" target="_blank" rel="noopener noreferrer" className="font-caps link-underline" style={{ fontSize: ".58rem" }}>View site →</a>
            <span className="font-caps text-ash" style={{ fontSize: ".56rem" }}>{user?.email}</span>
          </div>
          {children}
        </main>
      </div>
    </div>
  );
};

// ------------------------------------------------------------- Dashboard
const StatCard = ({ label, value }) => (
  <div className="p-5" style={panel}><p className="font-caps text-ash" style={{ fontSize: ".55rem" }}>{label}</p><p className="font-display foil-text mt-2" style={{ fontSize: "2.2rem" }}>{value}</p></div>
);

const FanMap = ({ loc }) => {
  if (!loc || loc.located === 0) return null;
  return (
    <div className="mt-4 pt-3" style={{ borderTop: "var(--rule)" }} data-testid="fan-map">
      <p className="font-caps text-ash mb-2" style={{ fontSize: ".5rem" }}>Where fans are · {loc.located} located</p>
      <FanDots dots={loc.dots} testid="fan-map-svg" />
      <div className="grid gap-1 mt-3">
        {loc.places.slice(0, 6).map((p) => (
          <div key={p.name} className="grid grid-cols-[1fr_auto] items-center gap-3" style={{ fontSize: ".72rem" }} data-testid={`fan-place-${p.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
            <div className="min-w-0">
              <div className="flex justify-between"><span className="truncate" style={{ color: "var(--parchment)" }}>{p.name}</span><span className="text-gold">{p.count}</span></div>
              <div style={{ height: 3, background: "rgba(200,155,60,.15)", borderRadius: 2 }}><div style={{ height: 3, width: `${Math.max(6, (p.count / loc.places[0].count) * 100)}%`, background: "var(--gold)", borderRadius: 2 }} /></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const FanCounter = ({ fans }) => {
  if (!fans) return <StatCard label="Subscribers" value="—" />;
  const max = Math.max(1, ...fans.weekly.map((w) => w.count));
  return (
    <div className="p-5" style={panel} data-testid="fan-counter">
      <p className="font-caps text-ash flex items-center justify-between" style={{ fontSize: ".55rem" }}>
        <span>Fans</span><span className="text-gold">{fans.last_7d > 0 ? `+${fans.last_7d} this week` : "this week: 0"}</span>
      </p>
      <p className="font-display foil-text mt-2" style={{ fontSize: "2.2rem" }} data-testid="fan-total">{fans.total}</p>
      <p className="font-caps text-ash" style={{ fontSize: ".5rem" }}>subscribers · +{fans.last_30d} in 30 days</p>
      <div className="flex items-end gap-1 mt-3" style={{ height: 34 }} data-testid="fan-weekly" title="New subscribers per week, last 8 weeks">
        {fans.weekly.map((w) => (
          <span key={w.start} title={`Week of ${w.start}: ${w.count}`} style={{ flex: 1, height: `${Math.max(8, (w.count / max) * 100)}%`, background: w.count ? "var(--gold)" : "rgba(200,155,60,.2)", borderRadius: 1 }} />
        ))}
      </div>
      {fans.presaves.length > 0 && (
        <div className="mt-4 pt-3" style={{ borderTop: "var(--rule)" }} data-testid="presave-clicks">
          <p className="font-caps text-ash mb-1" style={{ fontSize: ".5rem" }}>Pre-save clicks</p>
          {fans.presaves.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-2" style={{ fontSize: ".78rem" }} data-testid={`presave-clicks-${p.id}`}>
              <Link to={`/admin/${p.upcoming ? "upcoming" : "releases"}/${p.id}`} className="link-underline truncate" style={{ color: "var(--parchment)" }}>{p.title}</Link>
              <span className="text-gold" style={{ fontFamily: "var(--display)" }}>{p.clicks}</span>
            </div>
          ))}
        </div>
      )}
      <FanMap loc={fans.locations} />
    </div>
  );
};

const ListCard = ({ label, items = [], emptyText = "Nothing yet", max = 8, basePath = "/admin/releases" }) => {
  const shown = items.slice(0, max);
  const more = Math.max(0, items.length - max);
  return (
    <div className="p-5" style={panel}>
      <p className="font-caps text-ash flex items-center justify-between" style={{ fontSize: ".55rem" }}>
        <span>{label}</span>
        <span className="text-gold" style={{ fontSize: ".55rem" }}>{items.length}</span>
      </p>
      {shown.length === 0 ? (
        <p className="font-display text-ash mt-3" style={{ fontSize: "1rem", fontStyle: "italic", opacity: 0.7 }}>{emptyText}</p>
      ) : (
        <ol className="mt-3 space-y-1.5" data-testid={`list-${label.replace(/\s+/g, "-").toLowerCase()}`}>
          {shown.map((item, idx) => (
            <li key={item.id} className="flex items-baseline gap-2" style={{ fontSize: ".82rem", color: "var(--parchment)" }}>
              <span className="text-gold font-caps flex-shrink-0" style={{ fontSize: ".65rem", minWidth: "1.4rem" }}>{idx + 1}.</span>
              <Link to={`${basePath}/${item.id}`} className="link-underline truncate" title={item.title} style={{ color: "var(--parchment)" }}>
                {item.title}
              </Link>
            </li>
          ))}
          {more > 0 && (
            <li className="font-caps text-ash pt-1" style={{ fontSize: ".6rem" }}>+{more} more</li>
          )}
        </ol>
      )}
    </div>
  );
};

const Dashboard = () => {
  const [d, setD] = useState(null);
  const load = () => { adminApi.get("/admin/dashboard").then(({ data }) => setD(data)).catch(() => {}); };
  useEffect(() => { load(); }, []);
  const cd = d?.next_release ? countdownParts(d.next_release.release_at) : null;
  const removeBlast = async (id, title) => {
    if (!window.confirm(`Remove "${title}" from your dashboard log? This does NOT recall the email — subscribers already received it. It only tidies up your admin view.`)) return;
    try {
      await adminApi.delete(`/admin/blasts/${id}`);
      toast.success("Blast entry removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div>
      <h1 className="font-display foil-text mb-6" style={{ fontSize: "2rem" }}>Dashboard</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/admin/releases/new" className="btn-gold justify-center" style={{ padding: "1.4rem" }} data-testid="quick-release"><Plus size={16} /> Add a release</Link>
        <Link to="/admin/upcoming/new" className="btn-gold justify-center" style={{ padding: "1.4rem" }} data-testid="quick-upcoming"><Plus size={16} /> Add upcoming</Link>
        <Link to="/admin/upcoming" className="btn-gold justify-center" style={{ padding: "1.4rem" }} data-testid="quick-video"><Film size={16} /> Music videos</Link>
        <Link to="/admin/merch/new" className="btn-gold justify-center" style={{ padding: "1.4rem" }} data-testid="quick-merch"><Plus size={16} /> Add a product</Link>
      </div>
      {d && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8 items-start">
            <ListCard label="Published" items={d.published_items || []} emptyText="No published songs" basePath="/admin/releases" />
            <ListCard label="Drafts" items={d.draft_items || []} emptyText="No drafts" basePath="/admin/releases" />
            <ListCard label="New this week" items={d.new_this_week_items || []} emptyText="No new songs this week" basePath="/admin/releases" />
            <FanCounter fans={d.fans} />
          </div>
          {d.next_release && (
            <div className="mt-6 p-5" style={panel}>
              <p className="font-caps text-ash" style={{ fontSize: ".55rem" }}>Next scheduled release</p>
              <p className="font-display text-gold-lift mt-2" style={{ fontSize: "1.4rem" }}>{d.next_release.title}</p>
              {cd && !cd.done && <p className="font-caps text-gold mt-2" style={{ fontSize: ".7rem" }}>{cd.days}d {cd.hours}h {cd.minutes}m to release</p>}
            </div>
          )}
          <BackupStatus last={d.last_backup} failure={d.last_backup_failure} />
          {d.most_shared && d.most_shared.length > 0 && <MostShared items={d.most_shared} />}
          {d.recent_blasts && d.recent_blasts.length > 0 && (
            <div className="mt-6 p-5" style={panel} data-testid="recent-blasts">
              <div className="flex items-center gap-2 mb-4">
                <Mail size={14} color="var(--gold)" />
                <p className="font-caps text-gold" style={{ fontSize: ".6rem" }}>Recent email blasts</p>
              </div>
              <div className="grid gap-3">
                {d.recent_blasts.map((b) => (
                  <div key={b.id} className="grid grid-cols-[1fr_auto_auto_auto_auto] items-center gap-4 py-2"
                       style={{ borderTop: "var(--rule)" }} data-testid={`blast-row-${b.id}`}>
                    <div>
                      <p style={{ color: "var(--parchment)", fontSize: ".9rem" }}>{b.title}</p>
                      <p className="font-caps text-ash" style={{ fontSize: ".55rem" }}>
                        {fmtShortDate(b.at)} · {(b.interests || []).join(", ") || "everyone"}
                      </p>
                    </div>
                    <BlastStat label="Sent" value={b.sent} />
                    <BlastStat label="Opens" value={`${b.opens || 0} · ${b.open_rate}%`} tone="gold" />
                    <BlastStat label="Clicks" value={`${b.clicks || 0} · ${b.click_rate}%`} tone="rose" />
                    <button
                      onClick={() => removeBlast(b.id, b.title)}
                      className="text-ash hover:text-rose transition-colors"
                      aria-label={`Remove ${b.title} from log`}
                      title="Remove from log (does not recall the email)"
                      data-testid={`blast-remove-${b.id}`}
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {!d.ffmpeg && <p className="mt-6 text-rose" style={{ fontSize: ".8rem" }}>Warning: audio processor (ffmpeg) unavailable — preview generation is disabled.</p>}
        </>
      )}
    </div>
  );
};

const BackupStatus = ({ last, failure }) => {
  const fmt = (iso) => new Date(iso).toLocaleString("en-US", { timeZone: "America/Phoenix", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  const stale = last && Date.now() - new Date(last.created_at).getTime() > 36 * 3600 * 1000;
  const bad = failure || stale || !last;
  const color = bad ? "#E2A03F" : "#7FBF7F";
  const text = failure ? `Last night's backup failed (${fmt(failure.at)}) — ` + (last ? `last good copy ${fmt(last.created_at)}` : "no copy saved yet")
    : !last ? "No backup yet — the first nightly copy runs tonight"
    : `Last backup: ${fmt(last.created_at)} ${stale ? "· overdue" : "✓"} · ${last.documents} records`;
  return (
    <div className="mt-6 px-5 py-3 flex flex-wrap items-center justify-between gap-3" style={{ ...panel, borderColor: bad ? "rgba(226,160,63,.5)" : undefined }} data-testid="backup-status" data-state={bad ? "warn" : "ok"}>
      <p className="flex items-center gap-2" style={{ color, fontSize: ".82rem" }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, display: "inline-block" }} />{text}
      </p>
      <Link to="/admin/settings" className="font-caps link-underline" style={{ fontSize: ".55rem", color: "var(--gold)" }} data-testid="backup-status-link">{bad ? "Back up now" : "Backups"}</Link>
    </div>
  );
};

const MostShared = ({ items }) => (
  <div className="mt-6 p-5" style={panel} data-testid="most-shared">
    <div className="flex items-center gap-2 mb-4">
      <Share2 size={14} color="var(--gold)" />
      <p className="font-caps text-gold" style={{ fontSize: ".6rem" }}>Most shared</p>
      <span className="font-caps text-ash" style={{ fontSize: ".5rem" }}>· share taps and link opens</span>
    </div>
    <div className="grid gap-2">
      {items.map((s) => (
        <div key={`${s.kind}-${s.id}`} className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-4 py-2" style={{ borderTop: "var(--rule)" }} data-testid={`shared-row-${s.kind}-${s.id}`}>
          <div className="w-9 h-9 overflow-hidden grid place-items-center" style={{ background: "#000", borderRadius: "var(--radius)" }}>
            {s.image ? <img src={mediaUrl(s.image)} alt="" className="w-full h-full object-cover" /> : <Music size={14} color="var(--ash)" />}
          </div>
          <div className="min-w-0">
            <Link to={s.kind === "album" ? `/admin/releases/${s.id}` : `/admin/merch/${s.id}`} className="link-underline truncate block" style={{ color: "var(--parchment)", fontSize: ".9rem" }}>{s.title}</Link>
            <p className="font-caps text-ash" style={{ fontSize: ".5rem" }}>{s.kind === "album" ? "Song" : "Merch"}</p>
          </div>
          <BlastStat label="Shared" value={s.shares} tone="gold" />
          <BlastStat label="Opened" value={s.opens} tone="rose" />
        </div>
      ))}
    </div>
  </div>
);

const BlastStat = ({ label, value, tone }) => (
  <div className="text-right">
    <p className="font-caps text-ash" style={{ fontSize: ".5rem" }}>{label}</p>
    <p style={{ color: tone === "gold" ? "var(--gold)" : tone === "rose" ? "var(--rose)" : "var(--parchment)", fontSize: ".82rem", fontFamily: "var(--display)" }}>{value}</p>
  </div>
);

// ------------------------------------------------------------- Collection list
// Warns when more than one release is Featured (hero/Listen Everywhere only honour one).
const FeaturedWarning = ({ items, onFixed }) => {
  const featured = items.filter((r) => r.featured && !r.is_deleted);
  const [busy, setBusy] = useState(null);
  if (featured.length < 2) return null;
  const keepOnly = async (keep) => {
    setBusy(keep.id);
    try {
      await Promise.all(featured.filter((r) => r.id !== keep.id).map((r) => adminApi.put(`/admin/releases/${r.id}`, { featured: false })));
      toast.success(`"${keep.title}" is now the only Featured release`);
      onFixed?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  return (
    <div className="mb-6 p-4 flex flex-col gap-3" style={{ border: "1px solid var(--gold)", background: "rgba(200,155,60,.08)", borderRadius: "var(--radius)" }} data-testid="featured-warning">
      <p className="font-caps text-gold" style={{ fontSize: ".6rem" }}>Heads up — {featured.length} releases are marked Featured</p>
      <p style={{ color: "var(--parchment)", fontSize: ".85rem" }}>The homepage hero and Listen Everywhere only follow one Featured release. Pick which one should stay featured:</p>
      <div className="flex flex-wrap gap-2">
        {featured.map((r) => (
          <Btn key={r.id} variant="ghost" type="button" disabled={Boolean(busy)} onClick={() => keepOnly(r)} data-testid={`keep-featured-${r.id}`}>
            {busy === r.id ? "Fixing…" : `Keep only "${r.title}"`}
          </Btn>
        ))}
      </div>
    </div>
  );
};

const CollectionList = ({ title, collection, basePath, filter }) => {
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [trash, setTrash] = useState([]);
  const [showTrash, setShowTrash] = useState(false);
  const load = () => {
    adminApi.get(`/admin/${collection}`).then(({ data }) => { setAllItems(data); setItems(filter ? data.filter(filter) : data); }).catch(() => {});
    adminApi.get(`/admin/${collection}?trash=true`).then(({ data }) => setTrash(data)).catch(() => {});
  };
  // Reset lists + refetch whenever we switch between Releases ↔ Upcoming (they share the
  // same `collection="releases"` so we key the effect on `basePath` to force a clean reload)
  useEffect(() => {
    setItems([]);
    setTrash([]);
    setShowTrash(false);
    load();
  }, [collection, basePath]);
  const del = async (id) => { if (!window.confirm("Move to trash?")) return; await adminApi.delete(`/admin/${collection}/${id}`); toast.success("Moved to trash"); load(); };
  const restore = async (id) => { await adminApi.post(`/admin/${collection}/${id}/restore`); toast.success("Restored"); load(); };
  const list = showTrash ? trash : items;
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display foil-text" style={{ fontSize: "2rem" }}>{title}</h1>
        <Link to={`${basePath}/new`} className="btn-gold" data-testid={`new-${collection}`}><Plus size={15} /> New</Link>
      </div>
      {collection === "releases" && <FeaturedWarning items={allItems} onFixed={load} />}
      <div className="flex gap-4 mb-4 font-caps" style={{ fontSize: ".56rem" }}>
        <button onClick={() => setShowTrash(false)} style={{ color: showTrash ? "var(--ash)" : "var(--gold)", background: "none", border: "none", cursor: "pointer" }}>Active ({items.length})</button>
        <button onClick={() => setShowTrash(true)} style={{ color: showTrash ? "var(--gold)" : "var(--ash)", background: "none", border: "none", cursor: "pointer" }}>Trash ({trash.length})</button>
      </div>
      <div className="grid gap-3">
        {list.map((r) => (
          <div key={r.id} className="p-3 flex items-center gap-4" style={panel} data-testid={`row-${r.id}`}>
            <div className="w-12 h-12 grid place-items-center overflow-hidden shrink-0" style={{ background: "#000", borderRadius: "var(--radius)" }}>
              {r.artwork_url || r.image_url || r.poster_url ? <img src={mediaUrl(r.artwork_url || r.image_url || r.poster_url)} alt="" className="w-full h-full object-cover" /> : <Music size={16} color="var(--ash)" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="truncate" style={{ color: "var(--parchment)" }}>{r.title || r.name}</p>
              <p style={{ color: "var(--ash)", fontSize: ".7rem" }}>{r.type || r.source_type || r.category} {r.release_at ? `· ${fmtShortDate(r.release_at)}` : ""} {r.price ? `· $${r.price}` : ""}</p>
            </div>
            {r.status && <StatusPill status={r.status} />}
            {basePath === "/admin/upcoming" && !showTrash && <PreflightBadge release={r} />}
            {(r.video?.primary || r.video?.widescreen || r.video?.youtube_url) && (
              <span className="font-caps inline-flex items-center gap-1" title={r.video.visible ? "Video visible on site" : "Video attached, hidden"}
                    style={{ fontSize: ".55rem", color: r.video.visible ? "var(--gold)" : "var(--ash)", border: `1px solid ${r.video.visible ? "var(--gold)" : "var(--ash)"}`, borderRadius: "var(--radius)", padding: ".25rem .5rem", letterSpacing: ".14em", fontWeight: 700 }}
                    data-testid={`video-badge-${r.id}`}>
                <Film size={10} /> VIDEO {r.video.visible ? "ON" : "OFF"}
              </span>
            )}
            {r.presave && (
              <span
                className="font-caps"
                style={{ fontSize: ".55rem", color: "var(--gold)", border: "1px solid var(--gold)", borderRadius: "var(--radius)", padding: "0.25rem 0.5rem", letterSpacing: ".14em", fontWeight: 700 }}
                title="Pre-save link is configured"
                data-testid={`presave-badge-${r.id}`}
              >
                PRE-SAVE ✓
              </span>
            )}
            {r.featured && (
              <span
                className="inline-flex items-center gap-1 px-2 py-1 font-caps"
                style={{ fontSize: ".55rem", color: "#000", background: "var(--gold)", border: "1px solid var(--gold)", borderRadius: "var(--radius)", letterSpacing: ".14em", fontWeight: 700 }}
                data-testid={`featured-badge-${r.id}`}
                title="Featured on hero"
              >
                <span style={{ fontSize: ".9rem", lineHeight: 1 }}>★</span> FEATURED
              </span>
            )}
            {showTrash ? (
              <button onClick={() => restore(r.id)} className="text-gold" aria-label="Restore" data-testid={`restore-${r.id}`}><RotateCcw size={16} /></button>
            ) : (
              <>
                <Link to={`${basePath}/${r.id}`} className="text-gold" aria-label="Edit" data-testid={`edit-${r.id}`}><Pencil size={16} /></Link>
                <button onClick={() => del(r.id)} className="text-rose" aria-label="Delete" data-testid={`del-${r.id}`}><Trash2 size={16} /></button>
              </>
            )}
          </div>
        ))}
        {list.length === 0 && <p style={{ color: "var(--ash)" }}>Nothing here yet.</p>}
      </div>
      {basePath === "/admin/upcoming" && <UnassignedVideos releases={allItems} onAttached={load} />}
    </div>
  );
};

// ------------------------------------------------------------- Pages editor
const PagesEditor = () => {
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { adminApi.get("/admin/singleton/pages").then(({ data }) => setData(data.data || {})).catch(() => {}); }, []);
  const set = (k, v) => setData((p) => ({ ...p, [k]: v }));
  const save = async () => { setSaving(true); try { await adminApi.put("/admin/singleton/pages", { data }); toast.success("Saved. Live within seconds."); } catch { toast.error("Save failed"); } finally { setSaving(false); } };
  return (
    <div className="max-w-[680px]">
      <div className="flex items-center justify-between mb-6"><h1 className="font-display foil-text" style={{ fontSize: "2rem" }}>Pages & copy</h1><Btn onClick={save} disabled={saving} data-testid="save-pages">{saving ? "Saving…" : "Save"}</Btn></div>
      <div className="p-5 grid gap-4" style={panel}>
        <p className="font-caps text-gold" style={{ fontSize: ".6rem" }}>Hero</p>
        <Field label="Eyebrow"><Text value={data.hero_eyebrow || ""} onChange={(e) => set("hero_eyebrow", e.target.value)} data-testid="pg-hero_eyebrow" /></Field>
        <Field label="Headline"><Text value={data.hero_headline || ""} onChange={(e) => set("hero_headline", e.target.value)} data-testid="pg-hero_headline" /></Field>
        <Field label="Subhead"><Text value={data.hero_subhead || ""} onChange={(e) => set("hero_subhead", e.target.value)} data-testid="pg-hero_subhead" /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Primary button"><Text value={data.hero_primary || ""} onChange={(e) => set("hero_primary", e.target.value)} /></Field>
          <Field label="Secondary button"><Text value={data.hero_secondary || ""} onChange={(e) => set("hero_secondary", e.target.value)} /></Field>
        </div>
        <p className="font-caps text-gold mt-4" style={{ fontSize: ".6rem" }}>SEO</p>
        <Field label="Landing SEO title"><Text value={data.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} /></Field>
        <Field label="Landing SEO description"><Text value={data.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} /></Field>
      </div>
    </div>
  );
};

// ------------------------------------------------------------- Links editor
import { STREAMING, SOCIAL } from "@/data/platforms";
const LinksEditor = () => {
  const [data, setData] = useState({ streaming: {}, social: {}, smart_link: "", presave: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { adminApi.get("/admin/singleton/links").then(({ data }) => setData({ streaming: {}, social: {}, smart_link: "", presave: "", ...(data.data || {}) })).catch(() => {}); }, []);
  const save = async () => { setSaving(true); try { await adminApi.put("/admin/singleton/links", { data }); toast.success("Links saved"); } catch { toast.error("Save failed"); } finally { setSaving(false); } };
  return (
    <div className="max-w-[720px]">
      <div className="flex items-center justify-between mb-6"><h1 className="font-display foil-text" style={{ fontSize: "2rem" }}>Links</h1><Btn onClick={save} disabled={saving} data-testid="save-links">{saving ? "Saving…" : "Save"}</Btn></div>
      <div className="p-5 mb-5 grid sm:grid-cols-2 gap-4" style={panel}>
        <Field label="Smart link"><Text value={data.smart_link} onChange={(e) => setData({ ...data, smart_link: e.target.value })} placeholder="https://" /></Field>
        <Field label="Default pre-save"><Text value={data.presave} onChange={(e) => setData({ ...data, presave: e.target.value })} placeholder="https://" /></Field>
      </div>
      <div className="p-5 mb-5" style={panel}>
        <p className="font-caps text-gold mb-4" style={{ fontSize: ".6rem" }}>Streaming & stores</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {STREAMING.map((s) => <Field key={s.name} label={s.name}><Text value={(data.streaming || {})[s.name] || ""} onChange={(e) => setData({ ...data, streaming: { ...data.streaming, [s.name]: e.target.value } })} placeholder="https://" /></Field>)}
        </div>
      </div>
      <div className="p-5" style={panel}>
        <p className="font-caps text-gold mb-4" style={{ fontSize: ".6rem" }}>Social & video</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {SOCIAL.map((s) => <Field key={s.name} label={s.name}><Text value={(data.social || {})[s.name] || ""} onChange={(e) => setData({ ...data, social: { ...data.social, [s.name]: e.target.value } })} placeholder="https://" /></Field>)}
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------- Orders
const CARRIERS = ["USPS", "UPS", "FedEx", "DHL", "Other"];

const Orders = () => {
  const [rows, setRows] = useState([]);
  const [refundingId, setRefundingId] = useState(null);
  const [shipModal, setShipModal] = useState(null); // holds the order being shipped
  const load = () => adminApi.get("/admin/orders").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const totalPaid = rows.filter((r) => r.payment_status === "paid").reduce((s, r) => s + (r.amount || 0), 0);
  const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;
  const tone = (s) => (s === "paid" ? "var(--gold)" : s === "pending" ? "var(--ash)" : s === "refunded" ? "var(--rose)" : "var(--ash)");
  const refund = async (sid) => {
    if (!window.confirm("Fully refund this order? Public policy is 'all sales final' — use this only for damaged/lost/wrong-item cases.")) return;
    setRefundingId(sid);
    try {
      await adminApi.post(`/admin/orders/${sid}/refund`);
      toast.success("Order refunded");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Refund failed"); }
    finally { setRefundingId(null); }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display foil-text" style={{ fontSize: "2rem" }}>Orders ({rows.length})</h1>
        <div className="font-caps text-gold" style={{ fontSize: ".7rem" }} data-testid="orders-total-paid">Paid: {fmt(totalPaid)}</div>
      </div>
      <div className="grid gap-3">
        {rows.map((r) => {
          const shipped = r.fulfillment_status === "shipped";
          const canFulfill = r.payment_status === "paid" && !shipped;
          return (
            <div key={r.session_id} className="p-4 grid grid-cols-[1fr_auto_auto_auto_auto] items-start gap-4" style={panel} data-testid={`order-${r.session_id}`}>
              <div className="min-w-0">
                <p className="truncate" style={{ color: "var(--parchment)" }}>
                  {(r.items || []).map((it) => `${it.name}${it.size ? ` (${it.size})` : ""} × ${it.quantity}`).join(" · ") || "—"}
                </p>
                <p className="font-caps text-ash mt-1" style={{ fontSize: ".55rem" }}>
                  {fmtShortDate(r.created_at)} · {r.customer_email || "no email yet"}
                  {shipped && r.tracking_number && (
                    <>
                      {" · "}
                      <span style={{ color: "var(--gold)" }}>SHIPPED via {r.carrier}</span>
                      {" · "}
                      {r.tracking_url
                        ? <a href={r.tracking_url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--gold-lift)" }}>{r.tracking_number}</a>
                        : <span style={{ color: "var(--parchment)" }}>{r.tracking_number}</span>}
                    </>
                  )}
                </p>
                {r.shipping && (r.shipping.line1 || r.shipping.name) && (
                  <div className="mt-2 pt-2" style={{ borderTop: "var(--rule)" }} data-testid={`order-shipping-${r.session_id}`}>
                    <p className="font-caps text-ash mb-1" style={{ fontSize: ".5rem", letterSpacing: ".24em" }}>SHIP TO</p>
                    <div style={{ color: "var(--parchment)", fontSize: ".78rem", lineHeight: 1.4 }}>
                      {r.shipping.name && <div>{r.shipping.name}</div>}
                      {r.shipping.line1 && <div>{r.shipping.line1}</div>}
                      {r.shipping.line2 && <div>{r.shipping.line2}</div>}
                      <div>
                        {[r.shipping.city, r.shipping.state, r.shipping.postal_code].filter(Boolean).join(" ")}
                        {r.shipping.country && ` · ${r.shipping.country}`}
                      </div>
                      {r.shipping.phone && <div style={{ color: "var(--ash)", fontSize: ".72rem" }}>☎ {r.shipping.phone}</div>}
                    </div>
                  </div>
                )}
              </div>
              <span className="price-tag" style={{ fontSize: "1rem" }}>{fmt(r.amount || 0)}</span>
              <span className="font-caps" style={{ fontSize: ".55rem", color: tone(r.payment_status), letterSpacing: ".24em" }}>
                {(r.payment_status || "pending").toUpperCase()}
              </span>
              <div className="flex gap-2">
                {canFulfill && (
                  <button onClick={() => setShipModal(r)} data-testid={`ship-${r.session_id}`}
                          className="font-caps"
                          style={{ fontSize: ".55rem", color: "var(--gold)", background: "none",
                                   border: "1px solid rgba(200,155,60,.35)", borderRadius: "var(--radius)",
                                   padding: ".35rem .7rem", cursor: "pointer", letterSpacing: ".18em" }}>
                    Mark shipped
                  </button>
                )}
                {r.payment_status === "paid" && (
                  <button onClick={() => refund(r.session_id)} disabled={refundingId === r.session_id}
                          data-testid={`refund-${r.session_id}`}
                          className="font-caps"
                          style={{ fontSize: ".55rem", color: "var(--rose)", background: "none",
                                   border: "1px solid rgba(230,120,120,.35)", borderRadius: "var(--radius)",
                                   padding: ".35rem .7rem", cursor: "pointer", letterSpacing: ".18em" }}>
                    {refundingId === r.session_id ? "…" : "Refund"}
                  </button>
                )}
              </div>
              <span className="font-caps text-ash truncate" style={{ fontSize: ".5rem", maxWidth: 140 }}>{r.session_id.slice(-8)}</span>
            </div>
          );
        })}
        {rows.length === 0 && <p style={{ color: "var(--ash)" }}>No orders yet. Once a fan buys, it&rsquo;ll show here.</p>}
      </div>

      {shipModal && <ShipModal order={shipModal} onClose={() => setShipModal(null)} onDone={() => { setShipModal(null); load(); }} />}
    </div>
  );
};

const ShipModal = ({ order, onClose, onDone }) => {
  const [carrier, setCarrier] = useState("USPS");
  const [tn, setTn] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!tn.trim()) return toast.error("Tracking number is required");
    setBusy(true);
    try {
      const { data } = await adminApi.post(`/admin/orders/${order.session_id}/ship`,
                                           { carrier, tracking_number: tn.trim() });
      toast.success(data.email_sent ? "Marked shipped — customer emailed" : "Marked shipped (no email on file)");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not mark shipped"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4"
         style={{ background: "rgba(0,0,0,.72)", backdropFilter: "blur(6px)" }}
         data-testid="ship-modal">
      <form onSubmit={submit} className="p-6 max-w-[460px] w-full" style={{ ...panel, background: "var(--ink-raised)" }}>
        <p className="font-caps text-gold mb-1" style={{ fontSize: ".6rem" }}>Mark as shipped</p>
        <h2 className="font-display mb-4" style={{ fontSize: "1.3rem", color: "var(--parchment)" }}>
          Send tracking to {order.customer_email || "the customer"}
        </h2>
        <p className="mb-4" style={{ color: "var(--ash)", fontSize: ".78rem" }}>
          They&rsquo;ll get a branded email with the tracking link so they can follow the package.
        </p>
        <Field label="Carrier">
          <Select value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid="ship-carrier">
            {CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}
          </Select>
        </Field>
        <Field label="Tracking number">
          <Text value={tn} onChange={(e) => setTn(e.target.value)} data-testid="ship-tn"
                placeholder="e.g. 9400 1234 5678 9012 3456 78" />
        </Field>
        <div className="flex gap-3 justify-end mt-5">
          <Btn variant="ghost" type="button" onClick={onClose} disabled={busy} data-testid="ship-cancel">Cancel</Btn>
          <Btn type="submit" disabled={busy} data-testid="ship-submit">{busy ? "Sending…" : "Send tracking"}</Btn>
        </div>
      </form>
    </div>
  );
};

// ------------------------------------------------------------- Subscribers
const Subscribers = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { adminApi.get("/admin/subscribers").then(({ data }) => setRows(data)).catch(() => {}); }, []);
  const exportCsv = async () => {
    const { data } = await adminApi.get("/admin/subscribers.csv", { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a"); a.href = url; a.download = "subscribers.csv"; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-6"><h1 className="font-display foil-text" style={{ fontSize: "2rem" }}>Subscribers ({rows.length})</h1><Btn variant="ghost" onClick={exportCsv} data-testid="export-csv">Export CSV</Btn></div>
      <div className="grid gap-2">
        {rows.map((r) => (
          <div key={r.id} className="p-3 flex items-center gap-4" style={panel}>
            <span style={{ color: "var(--parchment)" }}>{r.email}</span>
            <span style={{ color: "var(--ash)", fontSize: ".75rem" }}>{r.first_name}</span>
            <span className="font-caps text-gold ml-auto" style={{ fontSize: ".55rem" }}>{r.interest}</span>
            <span style={{ color: "var(--ash)", fontSize: ".7rem" }}>{fmtShortDate(r.created_at)}</span>
          </div>
        ))}
        {rows.length === 0 && <p style={{ color: "var(--ash)" }}>No subscribers yet.</p>}
      </div>
    </div>
  );
};

// ------------------------------------------------------------- Settings
const TrustedDevices = ({ enabled }) => {
  const { listDevices, removeDevice } = useAuth();
  const [devices, setDevices] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => listDevices().then(setDevices).catch(() => setDevices([])), [listDevices]);
  useEffect(() => { if (enabled) load(); }, [enabled, load]);
  if (!enabled || devices === null) return null;
  const when = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const ago = (iso) => { const d = Math.floor((Date.now() - new Date(iso)) / 86400000); return d <= 0 ? "today" : d === 1 ? "yesterday" : `${d} days ago`; };
  const remove = async (d) => {
    setBusy(d.id);
    const res = await removeDevice(d);
    setBusy(null);
    if (res.ok) { toast.success(d.current ? "This browser will ask for a code next time" : "Device removed"); load(); }
    else toast.error(res.error);
  };
  return (
    <div className="mt-5 pt-4" style={{ borderTop: "var(--rule)" }} data-testid="trusted-devices">
      <p className="font-caps text-ash mb-2" style={{ fontSize: ".55rem" }}>Remembered devices ({devices.length})</p>
      {devices.length === 0 && <p style={{ color: "var(--ash)", fontSize: ".75rem" }} data-testid="trusted-devices-empty">No remembered devices — every sign-in asks for a code.</p>}
      {devices.map((d) => (
        <div key={d.id} className="flex items-center justify-between gap-3 py-2" style={{ borderTop: "var(--rule)", fontSize: ".78rem" }} data-testid={`device-${d.id}`}>
          <span style={{ color: "var(--parchment)" }}>
            {d.label}{d.current && <span className="font-caps text-gold" style={{ fontSize: ".5rem", marginLeft: 8 }} data-testid={`device-current-${d.id}`}>this browser</span>}
            <span className="block" style={{ color: "var(--ash)", fontSize: ".68rem" }}>added {when(d.created_at)} · last used {ago(d.last_used)}</span>
          </span>
          <Btn type="button" variant="ghost" disabled={busy === d.id} onClick={() => remove(d)} data-testid={`device-remove-${d.id}`} style={{ padding: ".4rem .8rem", fontSize: ".55rem" }}>
            {busy === d.id ? "…" : "Remove"}
          </Btn>
        </div>
      ))}
    </div>
  );
};

const TwoFAPanel = () => {
  const { user, setTwoFA, forgetDevices } = useAuth();
  const [pw, setPw] = useState(""); const [busy, setBusy] = useState(false);
  const [devKey, setDevKey] = useState(0);
  const enabled = user?.twofa_enabled !== false;
  const toggle = async () => {
    if (!pw) return toast.error("Enter your current password to confirm");
    setBusy(true);
    const res = await setTwoFA(!enabled, pw);
    setBusy(false);
    if (res.ok) { toast.success(enabled ? "Two-step sign-in turned off" : "Two-step sign-in turned on"); setPw(""); }
    else toast.error(res.error);
  };
  const forget = async () => {
    const res = await forgetDevices();
    if (res.ok) { toast.success("All remembered devices forgotten — you'll be asked for a code next time"); setDevKey((k) => k + 1); }
    else toast.error(res.error);
  };
  return (
    <div className="p-5 mb-5" style={panel} data-testid="twofa-panel">
      <p className="font-caps text-gold mb-2" style={{ fontSize: ".6rem" }}>Two-step sign-in (2FA)</p>
      <p className="mb-4" style={{ color: "var(--ash)", fontSize: ".78rem" }}>
        Status: <span style={{ color: enabled ? "var(--gold-lift)" : "var(--parchment)" }} data-testid="twofa-status">{enabled ? "On" : "Off"}</span>.
        {enabled ? " After your password, a 6-digit code is emailed to you. Devices you tick “remember” skip the code for 30 days." : " Only your password is required to sign in — we recommend keeping this on."}
      </p>
      <div className="grid gap-4">
        <Field label="Current password (to confirm)"><PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} data-testid="twofa-pw" /></Field>
        <div className="flex flex-wrap gap-3">
          <Btn type="button" variant={enabled ? "ghost" : undefined} disabled={busy} onClick={toggle} data-testid="twofa-toggle">
            {busy ? "Saving…" : enabled ? "Turn off two-step sign-in" : "Turn on two-step sign-in"}
          </Btn>
          {enabled && <Btn type="button" variant="ghost" onClick={forget} data-testid="twofa-forget-devices">Forget all remembered devices</Btn>}
        </div>
      </div>
      <TrustedDevices key={devKey} enabled={enabled} />
    </div>
  );
};

const DigestPanel = () => {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { const { data } = await adminApi.post("/admin/digest/send"); toast.success(`Digest sent to ${data.to} — check your inbox`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="p-5 mb-5" style={panel} data-testid="digest-panel">
      <p className="font-caps text-gold mb-2" style={{ fontSize: ".6rem" }}>Weekly digest</p>
      <p className="mb-4" style={{ color: "var(--ash)", fontSize: ".78rem" }}>Every Monday around 8 am Arizona time you get one email: new fans, merch orders, shares, pre-save clicks, the next drop and backup health. You also get an &ldquo;It&apos;s live&rdquo; summary the moment a scheduled release goes out.</p>
      <Btn type="button" variant="ghost" onClick={send} disabled={busy} data-testid="digest-send-now">{busy ? "Sending…" : "Email me this week's digest now"}</Btn>
    </div>
  );
};

const BackupsPanel = () => {
  const [items, setItems] = useState([]); const [busy, setBusy] = useState(false); const [dl, setDl] = useState(null);
  const load = useCallback(() => adminApi.get("/admin/backups").then(({ data }) => setItems(data.items)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const backupNow = async () => {
    setBusy(true);
    try { const { data } = await adminApi.post("/admin/backups"); toast.success(`Backup saved — ${data.documents} records`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const download = async (b) => {
    setDl(b.id);
    try {
      const res = await adminApi.get(`/admin/backups/${b.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `heartnsoul-backup-${b.id}.json.gz`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setDl(null); }
  };
  const [alertBusy, setAlertBusy] = useState(false);
  const testAlert = async () => {
    setAlertBusy(true);
    try { const { data } = await adminApi.post("/admin/backups/test-alert"); toast.success(`Test alert sent to ${data.to} — check your inbox`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setAlertBusy(false); }
  };
  const fmt = (iso) => new Date(iso).toLocaleString("en-US", { timeZone: "America/Phoenix", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  return (
    <div className="p-5 mb-5" style={panel} data-testid="backups-panel">
      <p className="font-caps text-gold mb-2" style={{ fontSize: ".6rem" }}>Backups</p>
      <p className="mb-4" style={{ color: "var(--ash)", fontSize: ".78rem" }}>A full copy of your releases, merch, subscribers and settings is saved automatically every night (about 2 am Arizona time). The last 30 are kept. If a nightly backup ever fails, you get an email alert (at most one per day).</p>
      <div className="flex flex-wrap gap-3">
        <Btn type="button" onClick={backupNow} disabled={busy} data-testid="backup-now">{busy ? "Backing up…" : "Back up now"}</Btn>
        <Btn type="button" variant="ghost" onClick={testAlert} disabled={alertBusy} data-testid="backup-test-alert">{alertBusy ? "Sending…" : "Send me a test alert"}</Btn>
      </div>
      <div className="mt-4 grid gap-2" data-testid="backup-list">
        {items.length === 0 && <p style={{ color: "var(--ash)", fontSize: ".75rem" }} data-testid="backup-empty">No backups yet — the first nightly copy runs tonight.</p>}
        {items.map((b) => (
          <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 py-2" style={{ borderTop: "var(--rule)", fontSize: ".78rem" }} data-testid={`backup-${b.id}`}>
            <span style={{ color: "var(--parchment)" }}>{fmt(b.created_at)} <span className="font-caps text-ash" style={{ fontSize: ".5rem", marginLeft: 8 }}>{b.trigger}</span></span>
            <span style={{ color: "var(--ash)" }}>{b.documents} records · {(b.bytes / 1024).toFixed(0)} KB</span>
            <Btn type="button" variant="ghost" onClick={() => download(b)} disabled={dl === b.id} data-testid={`backup-download-${b.id}`}>{dl === b.id ? "Preparing…" : "Download"}</Btn>
          </div>
        ))}
      </div>
    </div>
  );
};

const Settings = () => {
  const { user, changePassword, changeEmail, logoutEverywhere } = useAuth();
  const [cur, setCur] = useState(""); const [nw, setNw] = useState(""); const [busy, setBusy] = useState(false);
  const [emCur, setEmCur] = useState(""); const [emNew, setEmNew] = useState(""); const [emBusy, setEmBusy] = useState(false);
  const submit = async (e) => { e.preventDefault(); setBusy(true); const res = await changePassword({ current_password: cur, new_password: nw }); setBusy(false); if (res.ok) { toast.success("Password changed"); setCur(""); setNw(""); } else toast.error(res.error); };
  const submitEmail = async (e) => {
    e.preventDefault();
    if (!emNew || !emCur) return toast.error("Enter your new email and current password");
    setEmBusy(true);
    const res = await changeEmail({ current_password: emCur, new_email: emNew });
    setEmBusy(false);
    if (res.ok) { toast.success(`Sign-in email updated to ${res.user.email}`); setEmCur(""); setEmNew(""); }
    else toast.error(res.error);
  };
  return (
    <div className="max-w-[560px]">
      <h1 className="font-display foil-text mb-6" style={{ fontSize: "2rem" }}>Settings</h1>

      <TwoFAPanel />
      <BackupsPanel />
      <DigestPanel />

      <div className="p-5 mb-5" style={panel}>
        <p className="font-caps text-gold mb-4" style={{ fontSize: ".6rem" }}>Change sign-in email</p>
        <p className="mb-4" style={{ color: "var(--ash)", fontSize: ".72rem" }}>Current: <span style={{ color: "var(--parchment)" }}>{user?.email}</span></p>
        <form onSubmit={submitEmail} className="grid gap-4" data-testid="change-email-form">
          <Field label="New email address"><Text type="email" value={emNew} onChange={(e) => setEmNew(e.target.value)} data-testid="set-newemail" placeholder="you@yourdomain.com" /></Field>
          <Field label="Current password (to confirm)"><PasswordInput value={emCur} onChange={(e) => setEmCur(e.target.value)} data-testid="set-emailpw" /></Field>
          <Btn type="submit" disabled={emBusy} data-testid="set-changeemail">{emBusy ? "Saving…" : "Update email"}</Btn>
        </form>
      </div>

      <div className="p-5 mb-5" style={panel}>
        <p className="font-caps text-gold mb-4" style={{ fontSize: ".6rem" }}>Change password</p>
        <form onSubmit={submit} className="grid gap-4">
          <Field label="Current password"><PasswordInput value={cur} onChange={(e) => setCur(e.target.value)} data-testid="set-currentpw" /></Field>
          <Field label="New password"><PasswordInput value={nw} onChange={(e) => setNw(e.target.value)} data-testid="set-newpw" /></Field>
          <Btn type="submit" disabled={busy} data-testid="set-changepw">{busy ? "Saving…" : "Update password"}</Btn>
        </form>
      </div>
      <div className="p-5" style={panel}>
        <p className="font-caps text-gold mb-3" style={{ fontSize: ".6rem" }}>Sessions</p>
        <Btn variant="ghost" onClick={logoutEverywhere} data-testid="logout-everywhere">Log out everywhere</Btn>
      </div>
    </div>
  );
};

// ------------------------------------------------------------- Guard + routes
const Protected = ({ children }) => {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen grid place-items-center" style={{ backgroundColor: "var(--ink)", color: "var(--ash)" }}>Loading…</div>;
  if (!user) return <Navigate to="/admin" replace />;
  if (user.must_change_password) return <ForceChange />;
  return <AdminLayout>{children}</AdminLayout>;
};

const now = () => Date.now();
export default function AdminApp() {
  return (
    <Routes>
      <Route index element={<Login />} />
      <Route path="reset" element={<ResetPassword />} />
      <Route path="dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="releases" element={<Protected><CollectionList title="Releases" collection="releases" basePath="/admin/releases" filter={(r) => !r.release_at || new Date(r.release_at).getTime() <= now()} /></Protected>} />
      <Route path="releases/:id" element={<Protected><ReleaseEditor /></Protected>} />
      <Route path="upcoming" element={<Protected><CollectionList title="Upcoming" collection="releases" basePath="/admin/upcoming" filter={(r) => r.release_at && new Date(r.release_at).getTime() > now()} /></Protected>} />
      <Route path="upcoming/:id" element={<Protected><ReleaseEditor upcoming /></Protected>} />
      <Route path="videos" element={<Navigate to="/admin/upcoming" replace />} />
      <Route path="videos/:id" element={<Navigate to="/admin/upcoming" replace />} />
      <Route path="merch" element={<Protected><CollectionList title="Merch" collection="merch" basePath="/admin/merch" /></Protected>} />
      <Route path="merch/:id" element={<Protected><MerchEditor /></Protected>} />
      <Route path="pages" element={<Protected><PagesEditor /></Protected>} />
      <Route path="links" element={<Protected><LinksEditor /></Protected>} />
      <Route path="media" element={<Protected><MediaLibrary /></Protected>} />
      <Route path="subscribers" element={<Protected><Subscribers /></Protected>} />
      <Route path="orders" element={<Protected><Orders /></Protected>} />
      <Route path="settings" element={<Protected><Settings /></Protected>} />
      <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
    </Routes>
  );
}
